/**
 * @fileoverview Automatic drafting settings (MOR-2334/2336): switch daily
 * drafting on, choose which sources feed it, and connect / sync / disconnect
 * the Microsoft 365 account behind calendar, sent-mail and document
 * activity. Connection health is shown as connected, syncing, stale,
 * reconnect needed or unavailable.
 *
 * Dependencies:
 * - Convex: timesheets.activity.settings.*,
 *   integrations.microsoftActivity.{connections,actions}.*
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useMutation } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import { Loader2, RefreshCw, Settings2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  connectionHealth,
  type ConnectionHealth,
} from "@cvx/timesheets/activity/helpers";

import { extractErrorMessage } from "@/utils/errors";
import { Badge, Button } from "@/ui/primitives";
import { Label } from "@/ui/primitives/label";
import { Switch } from "@/ui/primitives/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { browserTimezone } from "./draftSuggestions.helpers";

type Health = ConnectionHealth;

const HEALTH_LABEL: Record<Health, string> = {
  connected: "Connected",
  syncing: "Syncing…",
  stale: "Stale",
  consent_required: "Reconnect needed",
  unavailable: "Unavailable",
  disconnected: "Disconnected",
};

const HEALTH_COLOR: Record<
  Health,
  "green" | "blue" | "amber" | "red" | "gray"
> = {
  connected: "green",
  syncing: "blue",
  stale: "amber",
  consent_required: "red",
  unavailable: "red",
  disconnected: "gray",
};

export function DraftingSettingsPopover({
  orgId,
}: {
  orgId: Id<"organizations">;
}) {
  const [open, setOpen] = useState(false);
  // Health depends on the wall clock; read it when the popover opens rather
  // than on every render, and never as a query argument (a changing argument
  // made each open a fresh query that briefly read as "not connected").
  const [openedAt, setOpenedAt] = useState(() => Date.now());

  const { data: settings } = useQuery(
    convexQuery(api.timesheets.activity.settings.mySettings, { orgId }),
  );
  const { data: connection, isError: connectionFailed } = useQuery(
    convexQuery(api.integrations.microsoftActivity.connections.myConnection, {
      orgId,
    }),
  );
  const updateSettings = useMutation(
    api.timesheets.activity.settings.updateSettings,
  );
  const setSources = useMutation(
    api.integrations.microsoftActivity.connections.setSources,
  );
  const getConnectUrl = useAction(
    api.integrations.microsoftActivity.actions.getConnectUrl,
  );
  const getAccountStatus = useAction(
    api.integrations.microsoftActivity.actions.getAccountStatus,
  );
  const connectAccount = useAction(
    api.integrations.microsoftActivity.actions.connectAccount,
  );
  const disconnectAccount = useAction(
    api.integrations.microsoftActivity.actions.disconnectAccount,
  );
  const syncNow = useAction(api.integrations.microsoftActivity.actions.syncNow);

  const [busy, setBusy] = useState<"connect" | "sync" | "disconnect" | null>(
    null,
  );
  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, []);

  const save = async (patch: Parameters<typeof updateSettings>[0]) => {
    try {
      await updateSettings(patch);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the setting."));
    }
  };

  const handleEnable = (enabled: boolean) =>
    void save({
      orgId,
      enabled,
      // Pin the practitioner's own timezone the first time they switch on.
      ...(enabled && settings?.enabled === false
        ? { timezone: browserTimezone() }
        : {}),
    });

  const toggleSource = (
    key: "morgan" | "calendar" | "mail" | "documents",
    on: boolean,
  ) => {
    if (!settings) return;
    void save({ orgId, sources: { ...settings.sources, [key]: on } });
    if (
      key !== "morgan" &&
      connection &&
      connection.status !== "disconnected"
    ) {
      void setSources({
        orgId,
        sources: { ...connection.sources, [key]: on },
      }).catch(() => {
        // The drafting toggle is authoritative; the connection mirror is best-effort.
      });
    }
  };

  const finishConnect = async () => {
    const result = await connectAccount({ orgId });
    if (result.ok) {
      toast.success(
        "Microsoft account connected — syncing the last two weeks.",
      );
    } else {
      toast.error(result.reason);
    }
  };

  const handleConnect = async () => {
    setBusy("connect");
    try {
      const status = await getAccountStatus({});
      if (status.status === "provider_not_configured") {
        toast.error(
          `The "${status.provider}" provider is not enabled in WorkOS (Pipes) yet.`,
        );
        setBusy(null);
        return;
      }
      if (status.status === "connected") {
        await finishConnect();
        setBusy(null);
        return;
      }
      const url = await getConnectUrl({});
      const popup = window.open(
        url,
        "morgan-pipes-connect",
        "width=600,height=720",
      );
      if (!popup) {
        toast.error("Popup blocked — allow popups for this site and retry.");
        setBusy(null);
        return;
      }
      pollRef.current = window.setInterval(() => {
        void (async () => {
          if (popup.closed) {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
            pollRef.current = null;
            setBusy(null);
            return;
          }
          const latest = await getAccountStatus({});
          if (latest.status === "connected") {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
            pollRef.current = null;
            popup.close();
            await finishConnect();
            setBusy(null);
          }
        })();
      }, 2500);
    } catch (err) {
      setBusy(null);
      toast.error(extractErrorMessage(err, "Could not start the connection."));
    }
  };

  const handleSync = async () => {
    setBusy("sync");
    try {
      await syncNow({ orgId });
      toast.success("Microsoft activity synced.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not sync."));
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    setBusy("disconnect");
    try {
      const result = await disconnectAccount({ orgId });
      toast.success(
        `Disconnected. ${result.deleted} activity record${result.deleted === 1 ? "" : "s"} deleted; ${result.retained} kept as evidence for accepted entries.`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not disconnect."));
    } finally {
      setBusy(null);
    }
  };

  // `undefined` means "not known yet", which is neither connected nor
  // disconnected: the connect / disconnect actions only render once the
  // query has answered.
  const checking = connection === undefined;
  const live =
    connection && connection.status !== "disconnected" ? connection : null;
  const health: Health = live
    ? connectionHealth(live, openedAt)
    : "disconnected";

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setOpenedAt(Date.now());
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8"
          aria-label="Drafting settings"
        >
          <Settings2 className="size-4" />
          Sources
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[22rem] max-w-[calc(100vw-2rem)] space-y-4 text-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label htmlFor="daily-drafting">Draft my day automatically</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Morgan will prepare suggestions for timesheets based on your
              activities for the previous day (AEST time). No timesheet entries
              are created until you accept them.
            </p>
            {settings?.lastSuccessfulRunAt !== undefined && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Last successful draft{" "}
                {formatDistanceToNow(settings.lastSuccessfulRunAt, {
                  addSuffix: true,
                })}
                .
              </p>
            )}
          </div>
          <Switch
            id="daily-drafting"
            checked={settings?.enabled ?? false}
            disabled={settings === undefined}
            onCheckedChange={handleEnable}
          />
        </div>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sources
          </legend>
          {(
            [
              [
                "morgan",
                "Morgan activity",
                "Monitors activity across the platform",
              ],
              ["calendar", "Outlook calendar", "Captures meeting data"],
              [
                "mail",
                "Outlook sent mail",
                "Emails you sent. Received mail is not read.",
              ],
              [
                "documents",
                "Microsoft 365 documents",
                "Word, Excel and PowerPoint files you edited.",
              ],
            ] as const
          ).map(([key, label, hint]) => (
            <div key={key} className="flex items-start justify-between gap-3">
              <div>
                <Label htmlFor={`source-${key}`}>{label}</Label>
                <p className="text-xs text-muted-foreground">{hint}</p>
              </div>
              <Switch
                id={`source-${key}`}
                checked={settings?.sources[key] ?? false}
                disabled={settings === undefined}
                onCheckedChange={(on) => toggleSource(key, on)}
              />
            </div>
          ))}
        </fieldset>

        <div className="space-y-2 rounded-md border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">Microsoft 365</span>
            {checking ? (
              <span
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
                role="status"
              >
                {!connectionFailed && (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                )}
                {connectionFailed
                  ? "Could not check the connection"
                  : "Checking connection…"}
              </span>
            ) : (
              <Badge color={HEALTH_COLOR[health]} variant="soft" size="sm">
                {HEALTH_LABEL[health]}
              </Badge>
            )}
          </div>
          {live?.accountEmail && (
            <p className="truncate text-xs text-muted-foreground">
              {live.accountEmail}
            </p>
          )}
          {live?.lastSyncError && (
            <p className="text-xs text-destructive" role="status">
              {live.lastSyncError}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Connect your account to provide read-only access to your calendar,
            sent mail, metadata and files.
          </p>
          <div className="flex flex-wrap gap-2">
            {checking ? null : live ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy !== null}
                  onClick={() => void handleSync()}
                >
                  {busy === "sync" ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3.5" />
                  )}
                  Sync now
                </Button>
                {health === "consent_required" && (
                  <Button
                    size="sm"
                    disabled={busy !== null}
                    onClick={() => void handleConnect()}
                  >
                    {busy === "connect" && (
                      <Loader2 className="size-3.5 animate-spin" />
                    )}
                    Reconnect
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={busy !== null}
                  onClick={() => void handleDisconnect()}
                >
                  {busy === "disconnect" && (
                    <Loader2 className="size-3.5 animate-spin" />
                  )}
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                disabled={busy !== null}
                onClick={() => void handleConnect()}
              >
                {busy === "connect" && (
                  <Loader2 className="size-3.5 animate-spin" />
                )}
                Connect Microsoft account
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
